import { create } from 'zustand'
import { persist, type PersistOptions } from 'zustand/middleware'

/**
 * Unified auth: one token for all users. GET /auth/me (refreshed every minute) provides
 * has_company and is_admin. Use getCustomerToken() (or getCompanyToken/getAdminToken) for API calls.
 */

/** Backend role values. */
export const ADMIN_ROLES = ['super_admin', 'admin', 'support'] as const
export type AdminRole = (typeof ADMIN_ROLES)[number]

export const COMPANY_ROLE = 'company' as const
export const CUSTOMER_ROLE = 'customer' as const

export type CompanyUser = {
  id: string
  email: string
  role: string
  company_id: string
  company_name: string
}

export type AdminUser = {
  id: string
  email: string
  role: string
}

export type CustomerUser = {
  id: string
  email: string
  role: string
}

/** From GET /auth/me; refreshed every minute so permission changes apply without re-login. */
export type MeInfo = {
  has_company: boolean
  is_admin: boolean
  company_id?: string | null
  company_name?: string | null
  locale?: string // bhs, hr, sr, en (backend); use bhs->bs for i18n
}

type AuthState = {
  customer: { token: string; user: CustomerUser } | null
  /** From /auth/me; not persisted. */
  me: MeInfo | null
  _hasHydrated: boolean
  setCustomerAuth: (token: string, user: CustomerUser) => void
  setMe: (me: MeInfo | null) => void
  logoutCustomer: () => void
  logoutCompany: () => void
  logoutAdmin: () => void
  /** Backward compat: stores token as customer. */
  setCompanyAuth: (token: string, user: CompanyUser) => void
  /** Backward compat: stores token as customer. */
  setAdminAuth: (token: string, user: AdminUser) => void
  setHasHydrated: (value: boolean) => void
}

const CUSTOMER_TOKEN_KEY = 'customer_token'

type PersistedAuth = Pick<AuthState, 'customer'>

export const useAuthStore = create<AuthState>()(
  persist(
    (set) => ({
      customer: null,
      me: null,
      _hasHydrated: false,
      setCustomerAuth: (token: string, user: CustomerUser) => {
        if (typeof localStorage !== 'undefined') {
          localStorage.setItem(CUSTOMER_TOKEN_KEY, token)
        }
        set({ customer: { token, user } })
      },
      setMe: (me: MeInfo | null) => set({ me }),
      logoutCustomer: () => {
        if (typeof localStorage !== 'undefined') {
          localStorage.removeItem(CUSTOMER_TOKEN_KEY)
        }
        set({ customer: null, me: null })
      },
      logoutCompany: () => useAuthStore.getState().logoutCustomer(),
      logoutAdmin: () => useAuthStore.getState().logoutCustomer(),
      setCompanyAuth: (token: string, user: CompanyUser) => {
        if (typeof localStorage !== 'undefined') localStorage.setItem(CUSTOMER_TOKEN_KEY, token)
        set({ customer: { token, user: { id: user.id, email: user.email, role: user.role } } })
      },
      setAdminAuth: (token: string, user: AdminUser) => {
        if (typeof localStorage !== 'undefined') localStorage.setItem(CUSTOMER_TOKEN_KEY, token)
        set({ customer: { token, user: { id: user.id, email: user.email, role: user.role } } })
      },
      setHasHydrated: (value: boolean) => set({ _hasHydrated: value }),
    }),
    {
      name: 'invoice-payer-auth',
      partialize: (s: AuthState) => ({ customer: s.customer }),
      onRehydrateStorage: () => (_state?: AuthState, _err?: unknown) => {
        useAuthStore.getState().setHasHydrated(true)
      },
      // Fallback: if rehydrate callback never runs (e.g. test env), mark hydrated after a short delay
      skipHydration: false,
    } as PersistOptions<AuthState, PersistedAuth>,
  ),
)

if (typeof window !== 'undefined') {
  const fallbackMs = 800
  setTimeout(() => {
    if (!useAuthStore.getState()._hasHydrated) {
      useAuthStore.getState().setHasHydrated(true)
    }
  }, fallbackMs)
}

/** Single token for all API calls (company, admin, customer). */
export function getCustomerToken(): string | null {
  return useAuthStore.getState().customer?.token ?? null
}

/** Same as getCustomerToken (one token for all). */
export function getCompanyToken(): string | null {
  return getCustomerToken()
}

/** Same as getCustomerToken (one token for all). */
export function getAdminToken(): string | null {
  return getCustomerToken()
}

/** Normalize backend role to lowercase for comparison. */
export function normalizeRole(role: string | undefined): string {
  return (role ?? '').toLowerCase()
}

export function isAdminRole(role: string): boolean {
  return ADMIN_ROLES.includes(normalizeRole(role) as AdminRole)
}

export function isCompanyRole(role: string): boolean {
  return normalizeRole(role) === COMPANY_ROLE
}

export function isCustomerRole(role: string): boolean {
  return normalizeRole(role) === CUSTOMER_ROLE
}

/**
 * Redirect path after login by namespace.
 * - Company login → /company
 * - Admin login → /admin
 * - Customer (future) → /dashboard or /
 */
export const LOGIN_REDIRECT: Record<string, string> = {
  company: '/company',
  admin: '/admin',
  customer: '/dashboard',
}
